import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { DropdownMenuItem } from '@trinity/chat-components/components/presentation/DropdownMenu';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ChatContextType } from '@trinity/chat';
import type { ChatContextItem } from '@trinity/chat';
import { ChatContextItemsContext } from '@trinity/chat-components/components/Chat/context';
import { HeaderContextContext } from '../context';
import { useSetGlobalChatContext } from './useSetGlobalChatContext';
import { ContextSelectorStyles } from '../styles';

type RendererProps = {
  onClick?: () => unknown;
};

export const useChatAgentMenuItems = (props: RendererProps) => {
  const dispatch = useContext(ChatStateDispatchContext);
  const {
    setPendingContextType,
    setPendingContext,
  } = useContext(HeaderContextContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const handleEverythingSelected = useSetGlobalChatContext({
    onClick: props.onClick,
  });

  const handleContextSelected = useCallback((item: ChatContextItem) => {
    return () => {
      dispatch({
        type: 'instance-context-changed',
        payload: {
          context: {
            type: ChatContextType.Agent,
            data: item,
            metadata: {},
          },
          brandIdentifier,
        },
      });
      setPendingContextType(null);
      setPendingContext(null);
      props.onClick?.();
    };
  }, [brandIdentifier, dispatch, props, setPendingContextType, setPendingContext]);

  const updatePendingAgent = useCallback((item: ChatContextItem, type: ChatContextType) => () => {
    setPendingContextType(type);
    setPendingContext(item);
    props.onClick?.();
  }, [props, setPendingContextType, setPendingContext]);

  const contextItems = useContext(ChatContextItemsContext);

  const getContextItemOnClick = useCallback((item: ChatContextItem) => {

    if (item.agent.identifier === ChatContextType.Global as string) {
      return handleEverythingSelected;
    };

    if ([
      ChatContextType.Document,
      ChatContextType.Folder,
      ChatContextType.Transcript,
      ChatContextType.KolProfile,
      ChatContextType.Project,
      ChatContextType.Brand,
    ].includes(item.agent.identifier as ChatContextType)) {
      return updatePendingAgent(item, item.agent.identifier as ChatContextType);
    }

    if (item.subjectAutocomplete?.required) {
      return updatePendingAgent(item, ChatContextType.Agent);
    }

    return handleContextSelected(item);
  }, [handleContextSelected, handleEverythingSelected, updatePendingAgent]);

  const renderMenuItem = useCallback((item: ChatContextItem) => {
    const onClick = getContextItemOnClick(item);
    return (
      <StyledDropdownMenuItem key={item.identifier} onClick={onClick}>
        {item.name}
      </StyledDropdownMenuItem>
    );
  }, [getContextItemOnClick]);

  const menuItems = useMemo(() => {
    return contextItems.map(renderMenuItem);
  }, [contextItems, renderMenuItem]);

  return menuItems;
};

const StyledDropdownMenuItem = styled(DropdownMenuItem)({
  textAlign: 'left',
  wordWrap: 'break-word',
  wordBreak: 'break-all',
  ...ContextSelectorStyles,
});